import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Button, EmptyState, Input, PageBody, Select, SkeletonText, Textarea, useToast } from "@astrum/ui";
import type { CustomField, FormQuestion, IntakeForm } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";

type Person = { id: string; displayName: string };
type Loaded = { form: IntakeForm; fields: CustomField[]; archived: boolean };

/** The in-app page for one intake form, at /forms/:formId. Any office member may fill it in. */
export function FormPage() {
  const { formId } = useParams();
  const { toast } = useToast();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [missing, setMissing] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ taskId: string } | null>(null);
  usePageTitle(loaded?.form.title ?? "Request");

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setMissing(false);
    setSent(null);
    setAnswers({});
    api
      .get<Loaded>(`/forms/${formId}`)
      .then((res) => !cancelled && setLoaded(res))
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [formId]);

  const needsPeople = loaded?.form.questions.some((q) => q.type === "person");
  useEffect(() => {
    if (!needsPeople) return;
    api
      .get<{ people: Person[] }>("/people")
      .then((res) => setPeople(res.people))
      .catch(() => setPeople([]));
  }, [needsPeople]);

  if (missing) {
    return (
      <PageBody title="Request" width="regular">
        <EmptyState icon="document" title="This form is not available" description="It may be switched off, or you may not have the link right." />
      </PageBody>
    );
  }
  if (!loaded) {
    return (
      <PageBody title="Request" width="regular">
        <SkeletonText lines={4} />
      </PageBody>
    );
  }
  const { form, fields, archived } = loaded;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const body: Record<string, string | number> = {};
    for (const q of form.questions) {
      const raw = (answers[q.id] ?? "").trim();
      if (raw === "") continue;
      body[q.id] = q.type === "number" ? Number(raw) : raw;
    }
    setBusy(true);
    try {
      const res = await api.post<{ task: { id: string } }>(`/forms/${form.id}/submissions`, { answers: body });
      setSent({ taskId: res.task.id });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Could not send your request.", { tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <PageBody title={form.title} width="regular">
        <EmptyState
          icon="check"
          title="Request sent"
          description="You can follow it from My requests."
          action={
            <>
              <Link to={`/work/task/${sent.taskId}`}>Open the task</Link>
              {" · "}
              <Link to="/forms">My requests</Link>
            </>
          }
        />
      </PageBody>
    );
  }

  const set = (q: FormQuestion, value: string) => setAnswers((prev) => ({ ...prev, [q.id]: value }));
  const label = (q: FormQuestion) => (q.required ? `${q.label} *` : q.label);

  return (
    <PageBody title={form.title} subtitle={form.description ?? `For ${form.projectName}`} width="regular">
      {archived || !form.enabled ? (
        <EmptyState icon="document" title="This form is not taking requests" />
      ) : (
        <form className="form-page" onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          {form.questions.map((q) => {
            const value = answers[q.id] ?? "";
            const field = q.target.kind === "custom" ? fields.find((f) => f.id === (q.target as { fieldId: string }).fieldId) : undefined;
            if (q.type === "long_text") {
              return <Textarea key={q.id} label={label(q)} hint={q.help} rows={4} value={value} onChange={(e) => set(q, e.target.value)} required={q.required} />;
            }
            if (q.type === "dropdown") {
              return (
                <Select key={q.id} label={label(q)} hint={q.help} value={value} onChange={(e) => set(q, e.target.value)} required={q.required}>
                  <option value="">Choose…</option>
                  {(field?.options ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              );
            }
            if (q.type === "person") {
              return (
                <Select key={q.id} label={label(q)} hint={q.help} value={value} onChange={(e) => set(q, e.target.value)} required={q.required}>
                  <option value="">Choose…</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </Select>
              );
            }
            return (
              <Input
                key={q.id}
                label={label(q)}
                hint={q.help}
                type={q.type === "date" ? "date" : "text"}
                inputMode={q.type === "number" ? "decimal" : undefined}
                value={value}
                onChange={(e) => set(q, e.target.value)}
                required={q.required}
              />
            );
          })}
          <div>
            <Button type="submit" disabled={busy}>
              Send request
            </Button>
          </div>
        </form>
      )}
    </PageBody>
  );
}
